import { clearComponentCache } from './iterator-cache';
import * as React from 'react';
import { Field } from './';
import { UiDefinition } from '../types-uidefinition';
import { UiSchema, EnabledChildEntityOperations } from '../types-uischema';
import { invariant } from '../../log';
import { RenderMode, SelectedValue } from 'app/types-shared';
import { FormContext } from 'app/infrastructure/ui/form/form-context';
import { debug } from 'app/infrastructure/log';
import { InspectionPanel } from 'app/infrastructure/ui/renderer/inspection-panel';
import { TabContainer, TabContainerDomId } from 'app/components';
import { SchemaBuilder } from '../builder';
import { EditState } from '../form/types';
import { ViewMode } from 'app/area/case/types-store';

const renderCaseTabs = (onClick) => <ul id={TabContainerDomId} role="tablist" onClick={onClick} />;

/**
 * Determine if component tree should be re-populated and re-connected to the store
 */
const shouldComponentsReconnect = (prev: ViewProps, next: ViewProps) => {
  return prev.renderMode !== next.renderMode || prev.schema !== next.schema;
};

/**
 * Indicates that we need to refactor
 */
const resolveRootDescriptor = (schema: UiSchema) => schema.children[0];

/**
 * Connects a root entity to the store and establishes a wrapping operating context.
 *
 * @export
 * @class View
 * @extends {React.Component<any, any>}
 */
class EntityViewComponent extends React.PureComponent<ViewProps, any> {

  constructor(props: ViewProps) {
    super(props);
    invariant(
      !!this.props.schema && !!this.props.schema.children.length,
      'Both dataSelector and schema must be supplied as props'
    );
    this.state = {};
  }

  public componentDidUpdate(prevProps: ViewProps) {
    debug('EntityView', 'new props', prevProps);
    const reconnect = shouldComponentsReconnect(prevProps, this.props);
    if (reconnect){
      debug('Will re-create VDOM');
      clearComponentCache();
    }
    this.setState({ reconnect });
  }

  public componentWillUnmount(){
    clearComponentCache();
  }

  private get finalProps() {
    return {
      ...this.props,
      ...{
        reconnect: this.state.reconnect,
        componentDescriptor: resolveRootDescriptor(this.props.schema)
      }
    } as any;
  }

  public render() {
    const {renderMode, uiDefinition} = this.props;
    const schema = SchemaBuilder.create(renderMode, uiDefinition);
    const moreThanOneTab = schema.availableSections.length > 1;
    const props = this.finalProps;
    props.hostProps = () => this.finalProps;

    // hide tabcontainer when rendering in print mode
    const TabControl = !props.print && <TabContainer
                          label={this.props.translation.case_menu}
                          visible={moreThanOneTab}
                          renderTabs={renderCaseTabs}
                          {...props} />;

    if (this.props.renderMode === RenderMode.edit) {
      return (
        <div className="entity-view">

          {TabControl}

          <FormContext
            key="edit-form"
            editing={this.props.editing}
            submit={this.props.onSubmit}
            cancel={this.props.onCancel}
            translation={this.props.translation}
          >
            <Field key="root-node" {...props} />
          </FormContext>

          {(this.props as any).$inspectedComponent &&  <InspectionPanel {...props} />}
        </div>
      );
    }
    return (
      <div className="entity-view">
        {TabControl}
        <Field {...props}/>
        {(this.props as any).$inspectedComponent &&  <InspectionPanel {...props} />}
      </div>
    );
  }
}

export { EntityViewComponent as EntityView };

export interface ViewProps {
  valueSelector?: (
    state: any,
    propertyPath: string,
    resolveContext?: any
  ) => SelectedValue;
  statePath: string;
  schema: UiSchema;
  actions: {};
  intl: any;
  editing: EditState;
  onSubmit: () => any;
  onCancel: () => any;
  renderMode: RenderMode;
  translation: any;
  enabledChildEntityOperations?: EnabledChildEntityOperations;
  uiDefinition: UiDefinition;
  changeViewMode: (mode: ViewMode) => void;
}
